import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {SerializedError} from '@reduxjs/toolkit';

interface BackendErrorData {
    message?: string;
    error?: string | {messages?: string[]};
    details?: string;
}

const getErrorFieldMessage = (
    errorField: BackendErrorData['error']
): string | undefined => {
    if (typeof errorField === 'string' && errorField !== '') {
        return errorField;
    }

    if (
        errorField &&
        typeof errorField === 'object' &&
        Array.isArray(errorField.messages) &&
        errorField.messages.length &&
        typeof errorField.messages[0] === 'string'
    ) {
        return errorField.messages[0];
    }

    return undefined;
};

const isBackendErrorData = (data: unknown): data is BackendErrorData => {
    if (typeof data !== 'object' || data === null) {
        return false;
    }

    const candidate = data as BackendErrorData;

    return (
        (typeof candidate.message === 'string' && candidate.message !== '') ||
        typeof candidate.error === 'string' ||
        (typeof candidate.error === 'object' &&
            candidate.error !== null &&
            Array.isArray(candidate.error.messages)) ||
        typeof candidate.details === 'string'
    );
};

const isFetchBaseQueryError = (
    error: unknown
): error is FetchBaseQueryError => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        'data' in error
    );
};

const isSerializedError = (error: unknown): error is SerializedError => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof (error as SerializedError).message === 'string'
    );
};

export const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }

    if (isFetchBaseQueryError(error)) {
        if (isBackendErrorData(error.data)) {
            return (
                error.data.message ||
                getErrorFieldMessage(error.data.error) ||
                error.data.details ||
                `HTTP ${error.status} error`
            );
        }
        return `HTTP ${error.status} error`;
    }

    if (isSerializedError(error)) {
        return error.message || 'Unknown error';
    }

    return 'An unexpected error occurred';
};
